import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { createEditor, EditorAPI } from './raceEditor';
import { PREFABS, RENDER_FINISH, LevelData, BodyConfig, transformSegmentBodies } from './levelData';

// ============================================================
// Debug logging helper
// ============================================================

const DEBUG = true;
function dbg(...args: unknown[]) {
    if (DEBUG) console.log('[Box3D-DEBUG]', ...args);
}

class PerfCounters {
    private physicsTimes: number[] = [];
    private syncTimes: number[] = [];
    private renderTimes: number[] = [];
    private frameCount = 0;
    private lastLogTime = performance.now();

    recordPhysics(dt: number) { this.physicsTimes.push(dt); }
    recordSync(ms: number) { this.syncTimes.push(ms); }
    recordRender(ms: number) { this.renderTimes.push(ms); }

    avg(arr: number[]): number {
        if (arr.length === 0) return 0;
        return arr.reduce((a, b) => a + b, 0) / arr.length;
    }

    log(marblePoss: string[]) {
        this.frameCount++;
        const now = performance.now();
        if (now - this.lastLogTime < 1000) return;

        const avgPhysics = (this.avg(this.physicsTimes) * 1000).toFixed(2);
        const avgSync = this.avg(this.syncTimes).toFixed(2);
        const avgRender = this.avg(this.renderTimes).toFixed(2);
        const fps = (this.frameCount / ((now - this.lastLogTime) / 1000)).toFixed(1);

        console.log(
            `%c[Box3D-PERF]%c ${fps}fps · phys ${avgPhysics}ms · sync ${avgSync}ms · render ${avgRender}ms · frame ${this.frameCount}`,
            'color:#4ade80;font-weight:bold', 'color:#888'
        );
        if (marblePoss.length > 0) {
            console.log(`%c[Box3D-MARBLES]%c ${marblePoss.join(' | ')}`, 'color:#60a5fa;font-weight:bold', 'color:#888');
        }

        this.physicsTimes = [];
        this.syncTimes = [];
        this.renderTimes = [];
        this.frameCount = 0;
        this.lastLogTime = now;
    }
}

const perf = new PerfCounters();

// ============================================================
// Box3D WASM Loader
// ============================================================

type BodyRecord = {
    position: { x: number; y: number; z: number };
    quaternion: { x: number; y: number; z: number; w: number };
    size: { x: number; y: number; z: number };
    shapeType: number;
    color: { r: number; g: number; b: number };
    isSensor: boolean;
    finished: boolean;
};

class Box3DPhysics {
    private module: any = null;
    private _wb3_init!: Function;
    private _wb3_step!: Function;
    private _wb3_reset!: Function;
    private _wb3_clear_world!: Function;
    private _wb3_add_box!: Function;
    private _wb3_add_rotated_box!: Function;
    private _wb3_add_axis_rotated_box!: Function;
    private _wb3_add_sphere!: Function;
    private _wb3_spawn_marbles!: Function;
    private _wb3_sync!: Function;
    private _wb3_set_gravity!: Function;
    private _wb3_get_body_count!: Function;
    private _wb3_get_stride!: Function;
    private _wb3_get_body_data!: Function;
    private _wb3_get_step_count!: Function;
    private _wb3_get_finished_count!: Function;
    private _wb3_get_finish_order!: Function;
    private _wb3_get_race_time!: Function;

    async init() {
        const t0 = performance.now();
        dbg('Loading WASM module...');

        const createMarbleRace = (window as any).createMarbleRace;
        if (!createMarbleRace) {
            throw new Error('createMarbleRace not found on window. WASM script may not have loaded.');
        }

        this.module = await createMarbleRace({
            locateFile: (path: string) => `/wasm/${path}`,
        });
        const t1 = performance.now();
        dbg(`WASM module loaded in ${(t1 - t0).toFixed(0)}ms`);

        this._wb3_init = this.module.cwrap('wb3_init', 'number', []);
        this._wb3_step = this.module.cwrap('wb3_step', null, ['number']);
        this._wb3_reset = this.module.cwrap('wb3_reset', null, []);
        this._wb3_clear_world = this.module.cwrap('wb3_clear_world', null, []);
        this._wb3_add_box = this.module.cwrap('wb3_add_box', 'number', ['number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number']);
        this._wb3_add_rotated_box = this.module.cwrap('wb3_add_rotated_box', 'number', ['number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number']);
        this._wb3_add_axis_rotated_box = this.module.cwrap('wb3_add_axis_rotated_box', 'number', ['number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number']);
        this._wb3_add_sphere = this.module.cwrap('wb3_add_sphere', 'number', ['number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number']);
        this._wb3_spawn_marbles = this.module.cwrap('wb3_spawn_marbles', 'number', ['number', 'number', 'number', 'number']);
        this._wb3_sync = this.module.cwrap('wb3_sync', null, []);
        this._wb3_set_gravity = this.module.cwrap('wb3_set_gravity', null, ['number']);
        this._wb3_get_body_count = this.module.cwrap('wb3_get_body_count', 'number', []);
        this._wb3_get_stride = this.module.cwrap('wb3_get_stride', 'number', []);
        this._wb3_get_body_data = this.module.cwrap('wb3_get_body_data', 'number', []);
        this._wb3_get_step_count = this.module.cwrap('wb3_get_step_count', 'number', []);
        this._wb3_get_finished_count = this.module.cwrap('wb3_get_finished_count', 'number', []);
        this._wb3_get_finish_order = this.module.cwrap('wb3_get_finish_order', 'number', ['number']);
        this._wb3_get_race_time = this.module.cwrap('wb3_get_race_time', 'number', []);

        dbg('cwrap bindings set up');

        // Don't init the world yet — we'll do it when building a course
        return this;
    }

    initWorld() {
        return this._wb3_init();
    }

    clearWorld() {
        this._wb3_clear_world();
    }

    addBox(bodyType: number, px: number, py: number, pz: number,
           hx: number, hy: number, hz: number,
           density: number, friction: number, restitution: number,
           shapeType: number, r: number, g: number, b: number,
           isSensor: number): number {
        return this._wb3_add_box(bodyType, px, py, pz, hx, hy, hz,
            density, friction, restitution,
            shapeType, r, g, b, isSensor);
    }

    addRotatedBox(bodyType: number, px: number, py: number, pz: number,
                  hx: number, hy: number, hz: number, angleX: number,
                  density: number, friction: number, restitution: number,
                  shapeType: number, r: number, g: number, b: number,
                  isSensor: number): number {
        return this._wb3_add_rotated_box(bodyType, px, py, pz, hx, hy, hz, angleX,
            density, friction, restitution,
            shapeType, r, g, b, isSensor);
    }

    addAxisRotatedBox(bodyType: number, px: number, py: number, pz: number,
                      hx: number, hy: number, hz: number,
                      axisX: number, axisY: number, axisZ: number, angle: number,
                      density: number, friction: number, restitution: number,
                      shapeType: number, r: number, g: number, b: number,
                      isSensor: number): number {
        return this._wb3_add_axis_rotated_box(bodyType, px, py, pz, hx, hy, hz,
            axisX, axisY, axisZ, angle,
            density, friction, restitution,
            shapeType, r, g, b, isSensor);
    }

    addSphere(bodyType: number, px: number, py: number, pz: number,
              radius: number,
              density: number, friction: number, restitution: number,
              shapeType: number, r: number, g: number, b: number,
              vx: number, vy: number, vz: number): number {
        return this._wb3_add_sphere(bodyType, px, py, pz, radius,
            density, friction, restitution,
            shapeType, r, g, b, vx, vy, vz);
    }

    spawnMarbles(startX: number, startY: number, startZ: number, halfDepthZ: number): number {
        return this._wb3_spawn_marbles(startX, startY, startZ, halfDepthZ);
    }

    sync() {
        this._wb3_sync();
    }

    step(dt: number = 1 / 60) {
        this._wb3_step(dt);
    }

    reset() { this._wb3_reset(); }
    setGravity(enabled: boolean) { this._wb3_set_gravity(enabled ? 1 : 0); }

    getBodyData(): Float32Array {
        const count = this._wb3_get_body_count();
        const stride = this._wb3_get_stride();
        const ptr = this._wb3_get_body_data();
        return new Float32Array(this.module.HEAPF32.buffer, ptr, count * stride);
    }

    getBodyCount(): number { return this._wb3_get_body_count(); }
    getStride(): number { return this._wb3_get_stride(); }
    getStepCount(): number { return this._wb3_get_step_count(); }
    getFinishedCount(): number { return this._wb3_get_finished_count(); }
    getFinishOrder(index: number): number { return this._wb3_get_finish_order(index); }
    getRaceTime(): number { return this._wb3_get_race_time(); }
}

// ============================================================
// Main Application
// ============================================================

const MARBLE_NAMES = ['Red', 'Blue', 'Green', 'Yellow', 'Orange', 'Purple', 'Pink', 'Cyan'];

const STRIDE = 16;
const RENDER_MARBLE = 5;

type AppMode = 'edit' | 'play';

let physics: Box3DPhysics;
let scene: THREE.Scene;
let camera: THREE.PerspectiveCamera;
let renderer: THREE.WebGLRenderer;
let marbleMeshes: THREE.InstancedMesh[] = [];
let trackMeshes: THREE.Mesh[] = [];
let controls: OrbitControls;
let editor: EditorAPI | null = null;
let mode: AppMode = 'edit';
let isRunning = false;
let finishedBannerShown = false;
let lastFinishedCount = 0;
let lastFrameTime = 0;
let currentLevelData: LevelData | null = null;

// ============================================================
// Scene setup
// ============================================================

function setupScene() {
    scene = new THREE.Scene();
    scene.background = new THREE.Color(0x1a1a22);

    camera = new THREE.PerspectiveCamera(40, window.innerWidth / window.innerHeight, 0.5, 100);
    camera.position.set(0, 22, 18);
    camera.lookAt(0, 2, 14);

    renderer = new THREE.WebGLRenderer({
        canvas: document.getElementById('scene') as HTMLCanvasElement,
        antialias: true,
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFShadowMap;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.2;

    controls = new OrbitControls(camera, renderer.domElement);
    controls.target.set(0, 3, 12);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.minDistance = 5;
    controls.maxDistance = 60;
    controls.maxPolarAngle = Math.PI / 2.1;
    controls.update();

    dbg('Scene setup complete');

    // Lighting
    const ambient = new THREE.AmbientLight(0x404060, 0.6);
    scene.add(ambient);

    const hemi = new THREE.HemisphereLight(0x88ccff, 0x442244, 0.8);
    scene.add(hemi);

    const sun = new THREE.DirectionalLight(0xffeedd, 2.0);
    sun.position.set(8, 20, 5);
    sun.castShadow = true;
    sun.shadow.mapSize.width = 1024;
    sun.shadow.mapSize.height = 1024;
    sun.shadow.camera.near = 0.5;
    sun.shadow.camera.far = 50;
    sun.shadow.camera.left = -20;
    sun.shadow.camera.right = 20;
    sun.shadow.camera.top = 20;
    sun.shadow.camera.bottom = -20;
    scene.add(sun);

    const fill = new THREE.DirectionalLight(0x8888ff, 0.4);
    fill.position.set(-5, 10, -5);
    scene.add(fill);

    window.addEventListener('resize', () => {
        const w = window.innerWidth;
        const h = window.innerHeight;
        camera.aspect = w / h;
        camera.updateProjectionMatrix();
        renderer.setSize(w, h);
    });
}

// ============================================================
// Physics → Scene sync (existing, unmodified logic)
// ============================================================

function syncPhysicsToScene() {
    if (!physics) return;
    const t0 = performance.now();
    const bodyData = physics.getBodyData();
    const count = physics.getBodyCount();
    const stride = physics.getStride();

    const marbleIndices: number[] = [];
    const trackBodies: Array<{
        pos: [number, number, number];
        quat: [number, number, number, number];
        size: [number, number, number];
        shapeType: number;
        color: [number, number, number];
        isSensor: boolean;
    }> = [];

    const marblePositions: string[] = [];

    for (let i = 0; i < count; i++) {
        const off = i * stride;
        const shapeType = bodyData[off + 10];
        if (shapeType === RENDER_MARBLE) {
            marbleIndices.push(i);
            const x = bodyData[off], y = bodyData[off + 1], z = bodyData[off + 2];
            const fin = bodyData[off + 15] > 0.5 ? '🏁' : '';
            marblePositions.push(`${MARBLE_NAMES[marbleIndices.length - 1] || i}(${x.toFixed(1)},${y.toFixed(1)},${z.toFixed(1)})${fin}`);
        } else {
            trackBodies.push({
                pos: [bodyData[off], bodyData[off + 1], bodyData[off + 2]],
                quat: [bodyData[off + 3], bodyData[off + 4], bodyData[off + 5], bodyData[off + 6]],
                size: [bodyData[off + 7], bodyData[off + 8], bodyData[off + 9]],
                shapeType: shapeType,
                color: [bodyData[off + 11], bodyData[off + 12], bodyData[off + 13]],
                isSensor: bodyData[off + 14] > 0.5,
            });
        }
    }

    while (trackMeshes.length < trackBodies.length) {
        const geo = new THREE.BoxGeometry(1, 1, 1);
        const mat = new THREE.MeshStandardMaterial({ roughness: 0.6, metalness: 0.05 });
        const mesh = new THREE.Mesh(geo, mat);
        mesh.castShadow = true;
        mesh.receiveShadow = true;
        scene.add(mesh);
        trackMeshes.push(mesh);
    }
    while (trackMeshes.length > trackBodies.length) {
        const m = trackMeshes.pop()!;
        scene.remove(m);
        m.geometry.dispose();
        (m.material as THREE.Material).dispose();
    }

    for (let i = 0; i < trackBodies.length; i++) {
        const tb = trackBodies[i];
        const mesh = trackMeshes[i];
        mesh.position.set(tb.pos[0], tb.pos[1], tb.pos[2]);
        mesh.quaternion.set(tb.quat[0], tb.quat[1], tb.quat[2], tb.quat[3]);
        mesh.scale.set(tb.size[0], tb.size[1], tb.size[2]);

        if (tb.isSensor) {
            (mesh.material as THREE.MeshStandardMaterial).color.setHex(0x33ee55);
            (mesh.material as THREE.MeshStandardMaterial).transparent = true;
            (mesh.material as THREE.MeshStandardMaterial).opacity = 0.3 + 0.2 * Math.sin(Date.now() * 0.003);
        } else {
            const c = tb.color;
            (mesh.material as THREE.MeshStandardMaterial).color.setRGB(c[0], c[1], c[2]);
            (mesh.material as THREE.MeshStandardMaterial).transparent = false;
        }
    }

    const marbleGeo = new THREE.SphereGeometry(1, 16, 12);

    for (const m of marbleMeshes) {
        scene.remove(m);
        m.geometry.dispose();
        (m.material as THREE.Material).dispose();
    }
    marbleMeshes = [];

    if (marbleIndices.length > 0) {
        const mat = new THREE.MeshStandardMaterial({
            roughness: 0.15,
            metalness: 0.5,
            color: 0xffffff,
            envMapIntensity: 0.6,
        });
        const instanced = new THREE.InstancedMesh(marbleGeo, mat, marbleIndices.length);
        instanced.castShadow = true;
        instanced.receiveShadow = true;
        instanced.frustumCulled = false;

        const dummy = new THREE.Object3D();
        for (let i = 0; i < marbleIndices.length; i++) {
            const bodyIdx = marbleIndices[i];
            const off = bodyIdx * stride;
            dummy.position.set(bodyData[off], bodyData[off + 1], bodyData[off + 2]);
            dummy.quaternion.set(bodyData[off + 3], bodyData[off + 4], bodyData[off + 5], bodyData[off + 6]);
            const r = bodyData[off + 7] * 0.5;
            dummy.scale.set(r, r, r);
            dummy.updateMatrix();
            instanced.setMatrixAt(i, dummy.matrix);

            const c = new THREE.Color(bodyData[off + 11], bodyData[off + 12], bodyData[off + 13]);
            instanced.setColorAt(i, c);
        }
        instanced.instanceMatrix.needsUpdate = true;
        if (instanced.instanceColor) instanced.instanceColor.needsUpdate = true;
        scene.add(instanced);
        marbleMeshes.push(instanced);
    }

    const syncTime = performance.now() - t0;
    perf.recordSync(syncTime);
    perf.log(marblePositions);
}

// ============================================================
// Race management
// ============================================================

function updateRaceLog() {
    const logEl = document.getElementById('race-log')!;
    const finished = physics.getFinishedCount();
    const time = physics.getRaceTime();
    const status = document.getElementById('status')!;

    status.innerHTML = `
        <span class="ok">●</span> Box3D ${physics.getBodyCount()} bodies · step ${physics.getStepCount()}<br>
        ⏱ ${time.toFixed(2)}s · ${finished}/8 finished
    `;

    let html = '<span class="label">═══ RACE LOG ═══</span><br>';
    if (finished > 0) {
        for (let i = 0; i < finished; i++) {
            const bodyIdx = physics.getFinishOrder(i);
            const name = i < MARBLE_NAMES.length ? MARBLE_NAMES[i] : `Marble ${bodyIdx}`;
            const medal = i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `${i + 1}.`;
            const data = physics.getBodyData();
            const off = bodyIdx * physics.getStride();
            const r = Math.round(data[off + 11] * 255);
            const g = Math.round(data[off + 12] * 255);
            const b = Math.round(data[off + 13] * 255);
            const hex = ((r << 16) | (g << 8) | b).toString(16).padStart(6, '0');
            html += `${medal} <span style="color:#${hex}">⬤</span> ${name}<br>`;
        }
    } else {
        html += '<span style="opacity:0.3">Waiting for race to start...</span>';
    }
    logEl.innerHTML = html;

    if (finished > 0 && finished !== lastFinishedCount) {
        lastFinishedCount = finished;
        if (finished >= 1 && !finishedBannerShown) {
            showWinner();
        }
    }
}

function showWinner() {
    const banner = document.getElementById('winner-banner')!;
    const nameEl = document.getElementById('winner-name')!;
    const timeEl = document.getElementById('winner-time')!;
    const listEl = document.getElementById('finish-list')!;

    const finCount = physics.getFinishedCount();
    const winnerBodyIdx = physics.getFinishOrder(0);
    let listHtml = '';

    const data = physics.getBodyData();
    const stride = physics.getStride();

    for (let i = 0; i < Math.min(8, finCount); i++) {
        const bodyIdx = physics.getFinishOrder(i);
        const name = i < MARBLE_NAMES.length ? MARBLE_NAMES[i] : `Marble ${bodyIdx}`;
        const medal = i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `${i + 1}.`;
        const off = bodyIdx * stride;
        const r = Math.round(data[off + 11] * 255);
        const g = Math.round(data[off + 12] * 255);
        const b = Math.round(data[off + 13] * 255);
        const hex = ((r << 16) | (g << 8) | b).toString(16).padStart(6, '0');
        listHtml += `<div>${medal} <span style="color:#${hex}">⬤</span> ${name}</div>`;
    }

    const winnerName = winnerBodyIdx < MARBLE_NAMES.length ? MARBLE_NAMES[winnerBodyIdx] : `Marble ${winnerBodyIdx}`;
    nameEl.textContent = `🏆 ${winnerName} Wins!`;
    timeEl.textContent = `Race time: ${physics.getRaceTime().toFixed(2)}s`;
    listEl.innerHTML = listHtml;
    banner.style.display = 'block';
    dbg(`Race finished! Winner: ${winnerName} in ${physics.getRaceTime().toFixed(2)}s`);
    finishedBannerShown = true;
}

function startRace() {
    dbg('Race started!');
    const banner = document.getElementById('winner-banner')!;
    banner.style.display = 'none';
    finishedBannerShown = false;
    lastFinishedCount = 0;
    isRunning = true;
    lastFrameTime = performance.now();
}

function resetRace() {
    dbg('Race reset');
    isRunning = false;
    physics.reset();
    const banner = document.getElementById('winner-banner')!;
    banner.style.display = 'none';
    finishedBannerShown = false;
    lastFinishedCount = 0;
    lastFrameTime = performance.now();
}

// ============================================================
// Build Box3D world from level data
// ============================================================

function buildWorldFromBodies(bodies: BodyConfig[], startInfo: { x: number, y: number, z: number, halfDepthZ: number } | null) {
    dbg(`Building Box3D world from ${bodies.length} body configs`);
    physics.initWorld();

    for (const b of bodies) {
        try {
            switch (b.func) {
                case 'box': {
                    const [hx, hy, hz] = b.halfExtents ?? [1, 1, 1];
                    const [px, py, pz] = b.position;
                    const [r, g, colB] = b.color;
                    physics.addBox(0, px, py, pz, hx, hy, hz,
                        b.density, b.friction, b.restitution,
                        b.shapeType, r, g, colB,
                        b.isSensor ? 1 : 0);
                    break;
                }
                case 'rotated_box': {
                    const [hx, hy, hz] = b.halfExtents ?? [1, 1, 1];
                    const [px, py, pz] = b.position;
                    const angleX = b.rotationX ?? 0;
                    const [r, g, colB] = b.color;
                    physics.addRotatedBox(0, px, py, pz, hx, hy, hz, angleX,
                        b.density, b.friction, b.restitution,
                        b.shapeType, r, g, colB,
                        b.isSensor ? 1 : 0);
                    break;
                }
                case 'axis_rotated_box': {
                    const [hx, hy, hz] = b.halfExtents ?? [1, 1, 1];
                    const [px, py, pz] = b.position;
                    const [ax, ay, az] = b.axis ?? [0, 1, 0];
                    const angle = b.angle ?? 0;
                    const [r, g, colB] = b.color;
                    physics.addAxisRotatedBox(0, px, py, pz, hx, hy, hz,
                        ax, ay, az, angle,
                        b.density, b.friction, b.restitution,
                        b.shapeType, r, g, colB,
                        b.isSensor ? 1 : 0);
                    break;
                }
                case 'sphere': {
                    const [px, py, pz] = b.position;
                    const radius = b.radius ?? 0.25;
                    const [r, g, colB] = b.color;
                    const vel = b.velocity ?? [0, 0, 0];
                    physics.addSphere(0, px, py, pz, radius,
                        b.density, b.friction, b.restitution,
                        b.shapeType, r, g, colB,
                        vel[0], vel[1], vel[2]);
                    break;
                }
            }
        } catch (e) {
            console.warn('[Box3D] Failed to add body:', e);
        }
    }

    // Spawn marbles at start platform
    if (startInfo) {
        const count = physics.spawnMarbles(startInfo.x, startInfo.y, startInfo.z, startInfo.halfDepthZ);
        dbg(`Spawned ${count} marbles at start platform`);
    }

    physics.sync();
    dbg(`World built: ${physics.getBodyCount()} bodies`);
}

// ============================================================
// Mode switching
// ============================================================

let savedLevelData: string | null = null; // JSON string backup for robust save/restore

function saveEditorState(src: LevelData) {
    // Deep-clone and validate, storing both JSON string and object
    const json = JSON.stringify(src);
    savedLevelData = json;
    currentLevelData = JSON.parse(json) as LevelData;
    dbg(`State saved: ${currentLevelData.segments.length} segments`);
    return currentLevelData;
}

function switchToPlay() {
    mode = 'play';
    dbg('=== SWITCH TO PLAY ===');
    
    // We MUST have data at this point. If currentLevelData is null, try saved backup
    let data: LevelData | null = currentLevelData;
    if (!data && savedLevelData) {
        data = JSON.parse(savedLevelData) as LevelData;
        currentLevelData = data;
    }
    
    // Deep-clone for the physics build so mutations don't affect our saved state
    const levelSnapshot = data ? JSON.parse(JSON.stringify(data)) : null;
    
    // Hide editor panel
    const panel = document.getElementById('editor-panel')!;
    panel.classList.add('hidden');

    // Show race controls and UI
    document.getElementById('btn-start')!.style.display = '';
    document.getElementById('btn-reset')!.style.display = '';
    document.getElementById('status')!.style.display = '';
    document.getElementById('race-log')!.style.display = '';

    // Update mode button
    document.getElementById('btn-edit-mode')!.textContent = '✏️ Edit';
    document.getElementById('btn-edit-mode')!.classList.remove('active-mode');

    // Destroy editor (cleans up event listeners and visual segments)
    if (editor) {
        editor.destroy();
        editor = null;
    }

    // Clear any leftover physics scene meshes
    for (const m of trackMeshes) {
        scene.remove(m);
        m.geometry.dispose();
        (m.material as THREE.Material).dispose();
    }
    trackMeshes = [];
    for (const m of marbleMeshes) {
        scene.remove(m);
        m.geometry.dispose();
        (m.material as THREE.Material).dispose();
    }
    marbleMeshes = [];

    // Build the physics world from current level data
    if (levelSnapshot && levelSnapshot.segments && levelSnapshot.segments.length > 0) {
        dbg(`Building physics world from ${levelSnapshot.segments.length} segments`);
        const bodies: BodyConfig[] = [];
        let startInfo: { x: number, y: number, z: number, halfDepthZ: number } | null = null;

        for (const seg of levelSnapshot.segments) {
            const prefab = PREFABS[seg.type];
            if (!prefab) {
                dbg(`Unknown segment type: ${seg.type}, skipping`);
                continue;
            }
            const segBodies = prefab.createBodyConfigs(seg);
            dbg(`  ${seg.type}: ${segBodies.length} raw bodies, rot=[${seg.rotation.map(r => r.toFixed(2))}], scale=[${(seg.scale ?? [1,1,1]).map(s => s.toFixed(2))}]`);
            const transformedBodies = transformSegmentBodies(segBodies, seg);
            dbg(`  → ${transformedBodies.length} transformed bodies`);
            bodies.push(...transformedBodies);

            if (seg.type === 'start_platform') {
                startInfo = {
                    x: seg.position[0],
                    y: seg.position[1],
                    z: seg.position[2],
                    halfDepthZ: seg.properties.depth / 2,
                };
            }
        }

        dbg(`Total bodies to build: ${bodies.length}`);
        if (bodies.length > 0) {
            buildWorldFromBodies(bodies, startInfo);
            isRunning = false;
            finishedBannerShown = false;
            lastFinishedCount = 0;
        } else {
            console.warn('[Box3D] No bodies to build — check segment types');
        }
    } else {
        dbg('No segments to build');
    }

    // Set camera for play view
    controls.target.set(0, 3, 12);
    controls.update();
}

function switchToEdit() {
    mode = 'edit';
    isRunning = false;
    dbg('=== SWITCH TO EDIT ===');

    // Show editor panel
    const panel = document.getElementById('editor-panel')!;
    panel.classList.remove('hidden');

    // Hide race controls
    document.getElementById('btn-start')!.style.display = 'none';
    document.getElementById('btn-reset')!.style.display = 'none';
    document.getElementById('winner-banner')!.style.display = 'none';

    // Update mode button
    document.getElementById('btn-edit-mode')!.textContent = '▶ Play';
    document.getElementById('btn-edit-mode')!.classList.add('active-mode');

    // Clear physics scene meshes
    for (const m of trackMeshes) {
        scene.remove(m);
        m.geometry.dispose();
        (m.material as THREE.Material).dispose();
    }
    trackMeshes = [];
    for (const m of marbleMeshes) {
        scene.remove(m);
        m.geometry.dispose();
        (m.material as THREE.Material).dispose();
    }
    marbleMeshes = [];

    // If we have no saved data, create empty default
    if (!currentLevelData && !savedLevelData) {
        currentLevelData = {
            name: 'My Course',
            author: 'Ray',
            version: 1,
            segments: [],
        };
        savedLevelData = JSON.stringify(currentLevelData);
    } else if (!currentLevelData && savedLevelData) {
        currentLevelData = JSON.parse(savedLevelData) as LevelData;
    }

    // Initialize editor with current level data
    editor = createEditor({
        scene,
        camera,
        renderer,
        controls,
        onBuildCourse: (data) => {
            dbg('Build button clicked — saving editor state');
            saveEditorState(data);
            switchToPlay();
        },
        onSegmentCountChange: (count) => {
            const el = document.getElementById('segment-count');
            if (el) el.textContent = `${count} segment${count !== 1 ? 's' : ''}`;
        },
    });

    // Restore segments into the new editor
    if (currentLevelData && currentLevelData.segments.length > 0) {
        dbg(`Restoring ${currentLevelData.segments.length} segments into editor`);
        editor.loadLevel(currentLevelData);
    } else {
        dbg('No segments to restore — starting fresh');
    }

    // Set camera for edit view
    controls.target.set(0, 3, 12);
    controls.update();
}

// ============================================================
// Main loop
// ============================================================

function animate() {
    requestAnimationFrame(animate);

    const now = performance.now();

    if (mode === 'play' && isRunning) {
        let dt = 1 / 60;
        if (lastFrameTime > 0) {
            dt = Math.min((now - lastFrameTime) / 1000, 1 / 30);
        }
        lastFrameTime = now;

        const tPhys = performance.now();
        physics.step(dt);
        perf.recordPhysics(dt);

        updateRaceLog();
    }

    // Only sync physics to scene in play mode
    if (mode === 'play' && physics) {
        // Pulse sensor materials in play mode
        for (const mesh of trackMeshes) {
            const mat = mesh.material as THREE.MeshStandardMaterial;
            if (mat.transparent && mat.color.getHex() === 0x33ee55) {
                mat.opacity = 0.3 + 0.2 * Math.sin(now * 0.003);
            }
        }

        const tRender = performance.now();
        syncPhysicsToScene();
        controls.update();
        renderer.render(scene, camera);
        perf.recordRender(performance.now() - tRender);
    } else if (mode === 'edit') {
        // In edit mode, just render the scene (editor handles its own objects)
        controls.update();
        renderer.render(scene, camera);
    } else {
        // Render even when paused in play mode
        controls.update();
        renderer.render(scene, camera);
    }
}

// ============================================================
// Boot
// ============================================================

async function boot() {
    console.log('%c[Box3D] ═══ Marble Race Test v2 — Editor ═══', 'color:#4ade80;font-weight:bold');
    console.log(`%c[Box3D] Three.js version: ${THREE.REVISION}`, 'color:#888');
    console.log(`%c[Box3D] Browser: ${navigator.userAgent}`, 'color:#888');
    console.log(`%c[Box3D] SharedArrayBuffer: ${typeof SharedArrayBuffer !== 'undefined' ? '✅ available' : '❌ NOT available'}`, 'color:#888');

    setupScene();

    const wasmStatus = document.getElementById('wasm-status')!;
    wasmStatus.textContent = '⏳ loading WASM...';
    wasmStatus.className = 'err';

    physics = new Box3DPhysics();
    await physics.init();

    wasmStatus.textContent = '● WASM active';
    wasmStatus.className = 'ok';

    // Wire up mode toggle
    document.getElementById('btn-edit-mode')?.addEventListener('click', () => {
        if (mode === 'edit') {
            if (editor) {
                dbg('Play button clicked — saving editor state');
                saveEditorState(editor.getLevelData());
            }
            switchToPlay();
        } else {
            switchToEdit();
        }
    });

    // Start in EDIT mode by default
    document.getElementById('btn-start')!.style.display = 'none';
    document.getElementById('btn-reset')!.style.display = 'none';

    document.getElementById('btn-start')?.addEventListener('click', startRace);
    document.getElementById('btn-reset')?.addEventListener('click', resetRace);

    lastFrameTime = performance.now();

    // Create default level: start with empty editor
    switchToEdit();

    animate();
}

boot().catch(err => {
    console.error('Boot failed:', err);
    const wasmStatus = document.getElementById('wasm-status')!;
    wasmStatus.textContent = '❌ ' + err.message;
    wasmStatus.className = 'err';
});
